import React, { useState, useCallback, useMemo, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useApiQuery } from '../../../utils/api'
import { useAvailableEpics, calculateEpicStats, useStartBatchAnalysis, usePrefetchEpicDetails, useAnalyzeEpic, epicQueryKeys } from '../../../utils/epicApi'
import { useEpicFilters } from '../../../utils/useEpicFilters'
import { useBatchState, useBatchQueue } from '../../../utils/useBatchState'
import { useEpicListNavigation, useRestoreNavigationState } from '../../../utils/useNavigationState'
import { useNotificationHandler, notificationUtils } from '../../../utils/useNotificationHandler'
import { Loading, Error } from '../../Elements/Indicators'
import { LoadingState, ProgressBar } from '../../Elements/ProgressIndicators'
import { EpicPageSkeleton, EpicFiltersSkeleton, EpicStatsSkeleton, BatchControllerSkeleton } from '../../Elements/EpicSkeletonLoaders'
import { EpicErrorBoundary } from '../../Elements/ErrorBoundary'
import EpicHeaderPills from '../../Elements/EpicHeaderPills'
import EpicFilters from './EpicFilters'
import { EpicStats } from './EpicStats'
import BatchController from './BatchController'
import EpicGrid from './EpicGrid'

import { XProject } from '@jirax/common'
import { NavigationState, EpicListItem } from '../../../types/epic-types'

interface ProjectData extends XProject {
  users: any[]
  lead: any
}

const EpicsPage: React.FC = memo(() => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Initialize notification handler
  useNotificationHandler()

  // Prefetch hook for epic details
  const prefetchEpicDetails = usePrefetchEpicDetails()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50 // Default page size

  // Track epics currently being analyzed
  const [analyzingEpics, setAnalyzingEpics] = useState<Set<string>>(new Set())

  // Fetch projects
  const {
    isLoading: projectsLoading,
    error: projectsError,
    data: projects
  } = useApiQuery<ProjectData[]>(['projects'])

  // Initialize filters with projects
  const {
    filters,
    setSelectedProject,
    setDateRange,
    isReady: filtersReady
  } = useEpicFilters({
    projects: projects || [],
    defaultProject: projects?.[0] || null
  })

  // Navigation state management
  const getCurrentNavigationState = useCallback((): NavigationState => ({
    scrollPosition: window.pageYOffset || document.documentElement.scrollTop || 0,
    currentPage,
    filters: {
      projectKey: filters.selectedProject?.key || '',
      dateRange: filters.dateRange
    }
  }), [currentPage, filters])

  const {
    saveState,
    restoreState,
    saveBeforeNavigation,
    restoreScrollPosition
  } = useEpicListNavigation(getCurrentNavigationState)

  // Restore navigation state on component mount
  useRestoreNavigationState(
    restoreState,
    (savedState) => {
      // Restore pagination state
      if (savedState.currentPage !== currentPage) {
        setCurrentPage(savedState.currentPage)
      }

      // Restore scroll position after a short delay to ensure DOM is ready
      setTimeout(() => {
        restoreScrollPosition(savedState.scrollPosition)
      }, 100)
    },
    [filtersReady] // Only restore when filters are ready
  )

  // Fetch epic data based on current filters
  const {
    isLoading: epicsLoading,
    error: epicsError,
    data: epicsResponse,
    refetch: refetchEpics,
    isFetching: epicsFetching
  } = useAvailableEpics({
    projectKey: filters.selectedProject?.key,
    startDate: filters.dateRange.startDate,
    endDate: filters.dateRange.endDate,
    enabled: filtersReady && !!filters.selectedProject
  })

  // Determine if filters should be disabled (during data fetching)
  const filtersDisabled = useMemo(() => {
    return projectsLoading || epicsFetching
  }, [projectsLoading, epicsFetching])

  // Calculate stats from epic data
  const stats = epicsResponse ? calculateEpicStats(epicsResponse) : {
    totalEpics: 0,
    analyzed: 0,
    notAnalyzed: 0,
    analysisPercentage: 0
  }

  // Batch state management
  const {
    activeBatch,
    isProcessing,
    error: batchError,
    startBatch,
    clearBatch,
    clearError: clearBatchError
  } = useBatchState({
    onBatchComplete: (batchId, success) => {
      console.log(`Batch ${batchId} completed with success: ${success}`)
      // Refetch epic data to update analysis status
      refetchEpics()
    },
    onBatchError: (batchId, error) => {
      console.error(`Batch ${batchId} failed:`, error)
    },
    enableNotifications: true
  })

  // Batch queue validation
  const { validateBatchRequest, validationError, clearValidationError } = useBatchQueue()

  // Start batch analysis mutation (optimistic updates disabled for batch operations)
  const startBatchMutation = useStartBatchAnalysis({
    enableOptimisticUpdates: false // Keep disabled for batch operations due to complexity
  })

  // Individual epic analysis mutation with optimistic updates
  const analyzeEpicMutation = useAnalyzeEpic({
    projectKey: filters.selectedProject?.key,
    startDate: filters.dateRange.startDate,
    endDate: filters.dateRange.endDate,
    enableOptimisticUpdates: true
  })

  // Handle batch start
  const handleBatchStart = async (request: any) => {
    // Clear any previous validation errors
    clearValidationError()
    clearBatchError()

    // Validate the request
    if (!validateBatchRequest(request)) {
      notificationUtils.warning(
        'Invalid Batch Request',
        validationError || 'Please check your batch parameters and try again.'
      )
      return
    }

    try {
      const result = await startBatchMutation.mutateAsync(request)
      if (result.success && result.batchId) {
        startBatch(result.batchId)
        notificationUtils.info(
          'Batch Analysis Started',
          `Processing ${result.summary?.totalEpicsFound || 'multiple'} epics. You'll be notified when complete.`
        )
      }
    } catch (error) {
      console.error('Failed to start batch analysis:', error)
      notificationUtils.error(
        'Failed to Start Batch Analysis',
        `Unable to queue batch analysis: ${(error as Error).message}`
      )
    }
  }

  // Handle batch cancel
  const handleBatchCancel = (batchId: string) => {
    // In a real implementation, this would call an API to cancel the batch
    console.log(`Cancelling batch ${batchId}`)
    clearBatch()
  }

  // Determine if we can start a batch (have unanalyzed epics)
  const canStartBatch = stats.notAnalyzed > 0 && !!filters.selectedProject && !isProcessing

  // Epic navigation handlers
  const handleEpicClick = useCallback((epicKey: string) => {
    // Save current state before navigation
    saveBeforeNavigation()
    // Navigate to epic details
    navigate(`/epics/${epicKey}`)
  }, [navigate, saveBeforeNavigation])

  // Handle epic hover for prefetching
  const handleEpicHover = useCallback((epicKey: string) => {
    // Only prefetch epic details if the epic has been analyzed
    if (epicsResponse?.success && epicsResponse.data?.epics) {
      const epic = epicsResponse.data.epics.find(item => item.key === epicKey)
      if (epic?.analysisStatus?.isAnalyzed) {
        prefetchEpicDetails(epicKey)
      }
    }
  }, [prefetchEpicDetails, epicsResponse])

  const handleAnalyzeEpic = useCallback(async (epicKey: string) => {
    // Add epic to analyzing set for UI feedback
    setAnalyzingEpics(prev => new Set(prev).add(epicKey))

    try {
      const result = await analyzeEpicMutation.mutateAsync(epicKey)

      if (result.success) {
        // Remove epic from analyzing set on success
        setAnalyzingEpics(prev => {
          const newSet = new Set(prev)
          newSet.delete(epicKey)
          return newSet
        })

        notificationUtils.success(
          'Epic Analysis Complete',
          `${epicKey} has been analyzed successfully.`
        )
      } else {
        // Remove epic from analyzing set on failure
        setAnalyzingEpics(prev => {
          const newSet = new Set(prev)
          newSet.delete(epicKey)
          return newSet
        })

        notificationUtils.error(
          'Epic Analysis Failed',
          result.message || `Unable to analyze ${epicKey}`
        )
      }
    } catch (error) {
      console.error(`Failed to analyze epic ${epicKey}:`, error)

      // Remove epic from analyzing set on error
      setAnalyzingEpics(prev => {
        const newSet = new Set(prev)
        newSet.delete(epicKey)
        return newSet
      })

      notificationUtils.error(
        'Epic Analysis Failed',
        `Unable to analyze ${epicKey}: ${(error as Error).message}`
      )
    }
  }, [analyzeEpicMutation])

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
    // Save state when page changes
    const newState = getCurrentNavigationState()
    newState.currentPage = page
    saveState(newState)

    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [getCurrentNavigationState, saveState])

  // Reset pagination when filters change
  const handleProjectChange = useCallback((project: XProject) => {
    setSelectedProject(project)
    setCurrentPage(1) // Reset to first page
  }, [setSelectedProject])

  const handleDateRangeChange = useCallback((range: { startDate: string; endDate: string }) => {
    setDateRange(range)
    setCurrentPage(1) // Reset to first page
  }, [setDateRange])

  // Prepare epic list data for display
  const epicListItems: EpicListItem[] = useMemo(() => {
    if (!epicsResponse?.success || !epicsResponse.data?.epics) {
      return []
    }

    return epicsResponse.data.epics.map(epic => ({
      key: epic.key,
      title: epic.title,
      projectKey: epic.project.key,
      completionDate: epic.completionDate,
      analysisStatus: epic.analysisStatus,
      // metrics will be undefined for AvailableEpic, only populated for analyzed epics
      metrics: undefined
    }))
  }, [epicsResponse])

  // Pagination data
  const totalItems = epicListItems.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize
  const paginatedEpics = epicListItems.slice(startIndex, endIndex)

  const paginationState = {
    page: currentPage,
    pageSize,
    totalItems,
    totalPages
  }

  // Handle loading states
  if (projectsLoading) {
    return <EpicPageSkeleton />
  }

  if (projectsError) {
    return <Error message={`Failed to load projects: ${(projectsError as Error).message}`} />
  }

  if (!projects || projects.length === 0) {
    return <Error message="No projects found. Please ensure you have access to at least one project." />
  }

  return (
    <>
      <main className="min-h-screen bg-white" role="main" aria-labelledby="epics-main-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {/* Page Header with Navigation Pills */}
          <header className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 id="epics-main-heading" className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                  Epic Reports
                </h1>
                <p className="text-sm sm:text-base text-gray-600 max-w-3xl">
                  Generate and view reports of epics across projects.
                </p>
              </div>
              <div className="flex-shrink-0">
                <EpicHeaderPills />
              </div>
            </div>
          </header>

          {/* Epic Filters - wrapped in error boundary */}
          <section aria-labelledby="epic-filters-section">
            <EpicErrorBoundary>
              <EpicFilters
                selectedProject={filters.selectedProject}
                dateRange={filters.dateRange}
                projects={projects}
                onProjectChange={handleProjectChange}
                onDateRangeChange={handleDateRangeChange}
                loading={!filtersReady}
                disabled={filtersDisabled}
              />
            </EpicErrorBoundary>
          </section>

          {/* Loading indicator for filter changes */}
          {epicsFetching && epicsResponse && (
            <div
              className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4"
              role="status"
              aria-live="polite"
              aria-label="Loading new data"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-blue-700">
                  Loading updated epic data...
                </p>
                <div className="flex-shrink-0">
                  <div
                    className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
                    aria-hidden="true"
                  ></div>
                </div>
              </div>
              <ProgressBar
                progress={50} // Indeterminate progress
                size="sm"
                color="blue"
                animated={true}
              />
            </div>
          )}

          {/* Stats and Batch Analysis - Combined horizontal layout */}
          <section aria-labelledby="stats-and-batch-section" className="mb-6">
            <h2 id="stats-and-batch-section" className="sr-only">Epic Statistics and Batch Analysis</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-24">
              {/* Epic Statistics - 1/2 width on large screens */}
              <div className="h-full">
                <EpicErrorBoundary>
                  <EpicStats
                    totalEpics={stats.totalEpics}
                    analyzedEpics={stats.analyzed}
                    analysisPercentage={stats.analysisPercentage}
                    loading={epicsLoading}
                  />
                </EpicErrorBoundary>
              </div>

              {/* Batch Controller - 1/2 width on large screens */}
              {filtersReady && filters.selectedProject && (
                <div className="h-full">
                  <EpicErrorBoundary>
                    <BatchController
                      projectKey={filters.selectedProject.key}
                      dateRange={filters.dateRange}
                      canStartBatch={canStartBatch}
                      activeBatch={activeBatch}
                      onBatchStart={handleBatchStart}
                      onBatchCancel={handleBatchCancel}
                    />
                  </EpicErrorBoundary>
                </div>
              )}
            </div>
          </section>

          {/* Enhanced error handling with retry functionality */}
          {epicsError && (
            <Error
              title="Failed to Load Epic Data"
              message={(epicsError as Error).message}
              onRetry={() => refetchEpics()}
              retryText="Retry Loading"
              actionable={true}
            />
          )}

          {batchError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start">
                <p className="text-red-800">{batchError}</p>
                <button
                  onClick={clearBatchError}
                  className="text-red-600 hover:text-red-800 text-sm underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {validationError && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start">
                <p className="text-yellow-800">{validationError}</p>
                <button
                  onClick={clearValidationError}
                  className="text-yellow-600 hover:text-yellow-800 text-sm underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Epic Grid - wrapped in error boundary */}
          {filtersReady && filters.selectedProject && (
            <section aria-labelledby="epic-list-section">
              <EpicErrorBoundary>
                <div className="mt-6 sm:mt-8">
                  <h2 id="epic-list-section" className="sr-only">Epic List</h2>
                  <EpicGrid
                    epics={paginatedEpics}
                    loading={epicsLoading}
                    onEpicClick={handleEpicClick}
                    onAnalyzeEpic={handleAnalyzeEpic}
                    onEpicHover={handleEpicHover}
                    pagination={paginationState}
                    onPageChange={handlePageChange}
                    analyzingEpics={analyzingEpics}
                    isBatchProcessing={isProcessing}
                  />
                </div>
              </EpicErrorBoundary>
            </section>
          )}

          {/* Debug info for development */}
          {process.env.NODE_ENV === 'development' && epicsResponse?.success && (
            <div className="mt-4 text-xs sm:text-sm text-gray-600 bg-gray-100 p-3 sm:p-4 rounded">
              <div className="space-y-1">
                <p>Found {stats.totalEpics} epics in {filters.selectedProject?.name}</p>
                <p>Date range: {filters.dateRange.startDate} to {filters.dateRange.endDate}</p>
                <p>Current page: {currentPage} of {totalPages}</p>
                <p>Showing {paginatedEpics.length} epics on this page</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  )
})

EpicsPage.displayName = 'EpicsPage'

export default EpicsPage